<template>
  <div class="progress-circular-wrapper" :style="`width: ${sizeNPixel}; height: ${sizeNPixel}`">
    <svg class="progress-circular" :style="`width: ${sizeNPixel}; height: ${sizeNPixel}`">
      <circle
        class="progress-circular-track"
        :cx="center"
        :cy="center"
        :r="radius"
        fill="transparent"
        stroke="rgba(0, 0, 0, 0.6)"
        :stroke-width="trackWidth"
        stroke-dasharray="0.1 5"
        stroke-linecap="round"
      />
      <circle
        class="progress-circular-indicator"
        :class="{ infinite }"
        :cx="center"
        :cy="center"
        :r="radius"
        fill="transparent"
        stroke="#005FB8"
        :stroke-width="indicatorWidth"
        :stroke-dasharray="dashArray"
        :stroke-dashoffset="dashOffset"
        stroke-linecap="round"
      />
    </svg>

    <div v-if="!hideInnerText" class="progress-circular-inner-text">
      <slot name="innerText" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'ProgressCircular',

  props: {
    progress: {
      type: Number,
      default: 0,
    },

    size: {
      type: String,
      default: 'large',
    },

    /** Whether the loader should show the progress or an infinite loading animation */
    infinite: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    trackWidth(): number {
      return 1.25;
    },

    indicatorWidth(): number {
      return 4;
    },

    sizeN(): number {
      if (this.size === 'small') {
        return 50;
      } else if (this.size === 'medium') {
        return 150;
      }

      // large
      return 391.35;
    },

    sizeNPixel(): string {
      return `${this.sizeN}px`;
    },

    center(): number {
      return this.sizeN / 2;
    },

    radius(): number {
      return this.center - (this.trackWidth > this.indicatorWidth ? this.trackWidth : this.indicatorWidth);
    },

    dashArray(): number {
      return 2 * Math.PI * this.radius;
    },

    dashOffset(): number {
      if (this.infinite) {
        // show 18% of the circle ((100 - 18) / 100 = 0.82)
        return this.dashArray * 0.82;
      }

      return this.dashArray * ((100 - this.progress) / 100);
    },

    hideInnerText(): Boolean {
      return !hasSlotContent(this.$slots.innerText, {}) || this.sizeN < 150;
    },
  },
});
</script>

<style lang="scss">
.progress-circular-wrapper {
  position: relative;
}

.progress-circular {
  transform: rotate(-90deg); /* Fix the orientation */
}

.progress-circular-inner-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

/* Infinite loading animation */
@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

.progress-circular-indicator {
  &.infinite {
    animation: rotate 4s linear infinite;
    transform-origin: center;
  }
}
</style>
